import React, { useState, useEffect, useRef } from 'react';
import { Clock, Plus, Pencil, Trash2, Send, CheckCircle, XCircle, RotateCcw, Settings2, Wrench, Layers, Printer, Filter } from 'lucide-react';
import { toast } from 'sonner';
import fetchWithAuth from '../../services/fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token');

const EVENT_CONFIG = {
  LineAdded:            { icon: Plus,        color: 'text-emerald-500', bg: 'bg-emerald-500/15', label: 'ADDED' },
  LineModified:         { icon: Pencil,      color: 'text-amber-500',   bg: 'bg-amber-500/15',   label: 'EDITED' },
  LineRemoved:          { icon: Trash2,      color: 'text-red-500',     bg: 'bg-red-500/15',     label: 'REMOVED' },
  Created:              { icon: Layers,      color: 'text-blue-500',    bg: 'bg-blue-500/15',    label: 'CREATED' },
  RevisionCreated:      { icon: Layers,      color: 'text-blue-500',    bg: 'bg-blue-500/15',    label: 'REVISION' },
  SubmittedForReview:   { icon: Send,        color: 'text-blue-500',    bg: 'bg-blue-500/15',    label: 'SUBMITTED' },
  ApprovalGranted:      { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-500/15', label: 'APPROVED' },
  ApprovalRejected:     { icon: XCircle,     color: 'text-red-500',     bg: 'bg-red-500/15',     label: 'REJECTED' },
  ReturnedToDraft:      { icon: RotateCcw,   color: 'text-amber-500',   bg: 'bg-amber-500/15',   label: 'RETURNED' },
  BudgetSet:            { icon: Settings2,   color: 'text-blue-500',    bg: 'bg-blue-500/15',    label: 'BUDGET' },
  StatusChanged:        { icon: CheckCircle, color: 'text-blue-500',    bg: 'bg-blue-500/15',    label: 'STATUS' },
  FabricationOpAdded:   { icon: Wrench,      color: 'text-orange-500',  bg: 'bg-orange-500/15',  label: 'FAB ADDED' },
  FabricationOpRemoved: { icon: Wrench,      color: 'text-red-500',     bg: 'bg-red-500/15',     label: 'FAB REMOVED' },
  FabricationOpModified:{ icon: Wrench,      color: 'text-amber-500',   bg: 'bg-amber-500/15',   label: 'FAB EDITED' },
  TemplateApplied:      { icon: Wrench,      color: 'text-cyan-500',    bg: 'bg-cyan-500/15',    label: 'TEMPLATE' },
};
const DEFAULT_CFG = { icon: Pencil, color: 'text-slate-400', bg: 'bg-slate-500/15', label: 'CHANGE' };

const formatTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const formatDate = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const groupByDay = (events) => {
  const groups = {};
  events.forEach(e => {
    const day = new Date(e.performed_at).toDateString();
    if (!groups[day]) groups[day] = [];
    groups[day].push(e);
  });
  return Object.entries(groups).map(([day, items]) => ({
    label: formatDate(items[0].performed_at),
    date: day,
    events: items,
  }));
};

const describeEvent = (e) => {
  const d = e.event_detail || {};
  const type = e.event_type;
  const partRef = d.part_number || d.part_id || '';
  const desc = d.description || '';

  switch (type) {
    case 'LineAdded': {
      const cls = d.make_buy || '';
      const qty = d.quantity || 1;
      return {
        title: d.is_assembly ? 'Assembly added' : `Part added (${cls})`,
        detail: `${partRef} ${desc}`.trim(),
        meta: `Qty: ${qty}`,
      };
    }
    case 'LineRemoved': {
      return {
        title: d.is_assembly ? 'Assembly removed' : 'Part removed',
        detail: `${partRef} ${desc}`.trim(),
        meta: d.quantity ? `Was Qty: ${d.quantity}` : '',
      };
    }
    case 'LineModified': {
      const changes = d.changes || {};
      const old = d.old_values || {};
      const keys = Object.keys(changes);
      const lines = keys.map(k => {
        const label = k === 'assembly_name' ? 'Name' : k === 'quantity' ? 'Qty' :
          k === 'material_profile_id' ? 'Material Profile' : k === 'surface_area_m2' ? 'Surface Area' :
          k === 'length_m' ? 'Length' : k === 'reference_designator' ? 'Ref Des' : k;
        const oldVal = old[k] !== undefined ? old[k] : '—';
        const newVal = changes[k];
        return `${label}: ${oldVal} → ${newVal}`;
      });
      const action = d.action;
      if (action === 'toggle_treatment') return { title: 'Treatment toggled', detail: `${d.rule} → ${d.is_selected ? 'ON' : 'OFF'}`, meta: partRef };
      if (action === 'add_treatment') return { title: 'Treatment added', detail: d.rule, meta: partRef };
      if (action === 'remove_treatment') return { title: 'Treatment removed', detail: d.rule, meta: partRef };
      if (action === 'reorder_reparent') return { title: 'Part moved/reordered', detail: `Line: ${d.line_id?.slice(0,8)}`, meta: '' };
      return {
        title: d.is_assembly ? 'Assembly edited' : 'Part edited',
        detail: `${partRef} ${desc}`.trim(),
        meta: lines.join(' │ '),
      };
    }
    case 'Created': return { title: 'BOM created', detail: '', meta: '' };
    case 'RevisionCreated': return { title: 'New revision created', detail: d.revision_number || '', meta: '' };
    case 'BudgetSet': {
      const parts = [];
      if (d.old_budget !== undefined || d.new_budget !== undefined) parts.push(`Cost: ${d.old_budget ?? d.old ?? '—'} → ${d.new_budget ?? d.new ?? '—'}`);
      if (d.old_weight_budget !== undefined) parts.push(`Weight: ${d.old_weight_budget} → ${d.new_weight_budget}`);
      return { title: 'Budget updated', detail: parts.join(' │ '), meta: d.reason || '' };
    }
    case 'SubmittedForReview': return { title: 'Submitted for review', detail: d.comment || '', meta: '' };
    case 'ApprovalGranted': return { title: `Approved (${d.stage || ''})`, detail: d.comment || '', meta: '' };
    case 'ApprovalRejected': return { title: `Rejected (${d.stage || ''})`, detail: d.reason || d.comment || '', meta: '' };
    case 'ReturnedToDraft': return { title: 'Returned to Draft', detail: d.comment || '', meta: '' };
    case 'StatusChanged': return { title: `Status → ${d.new_status || ''}`, detail: '', meta: '' };
    case 'FabricationOpAdded': return { title: 'Fabrication op added', detail: d.operation_name || '', meta: partRef };
    case 'FabricationOpRemoved': return { title: 'Fabrication op removed', detail: d.operation_name || '', meta: partRef };
    case 'FabricationOpModified': return { title: 'Fabrication op edited', detail: d.operation_name || '', meta: partRef };
    case 'TemplateApplied': return { title: 'Template applied', detail: d.template_name || '', meta: partRef };
    default: return { title: type.replace(/([A-Z])/g, ' $1').trim(), detail: JSON.stringify(d).slice(0, 80), meta: '' };
  }
};

const EventCard = ({ event }) => {
  const cfg = EVENT_CONFIG[event.event_type] || DEFAULT_CFG;
  const Icon = cfg.icon;
  const info = describeEvent(event);
  return (
    <div className="flex gap-2.5 py-2 px-1 group" data-testid={`activity-event-${event.event_id}`}>
      <div className={`w-6 h-6 rounded-full ${cfg.bg} flex items-center justify-center shrink-0 mt-0.5`}>
        <Icon className={`w-3 h-3 ${cfg.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`text-[10px] font-bold uppercase tracking-wider ${cfg.color}`}>{cfg.label}</span>
          <span className="text-[10px] text-slate-500 truncate">{info.title}</span>
        </div>
        {info.detail && <div className="text-[11px] text-slate-300 truncate mt-0.5">{info.detail}</div>}
        {info.meta && <div className="text-[10px] text-slate-500 mt-0.5 font-mono">{info.meta}</div>}
        <div className="text-[9px] text-slate-600 mt-0.5">
          {event.performed_by_name || event.performed_by} · {formatTime(event.performed_at)}
          {event.clause_references?.length > 0 && (
            <span className="text-amber-600/50 ml-1">[{event.clause_references.join(', ')}]</span>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Print Overlay ───────────────────────────────────────────── */
const PrintOverlay = ({ events, bomName, bomNumber, revisionLabel, onClose }) => {
  const printRef = useRef(null);
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const w = window.open('', '_blank', 'width=960,height=700');
    w.document.write(`<!DOCTYPE html><html><head><title>BOM Change History</title>
<style>* { margin: 0; padding: 0; box-sizing: border-box; } body { font-family: monospace; color: #222; padding: 24px; font-size: 11px; } @media print { body { padding: 12px; } }
table { width: 100%; border-collapse: collapse; margin-top: 8px; } th, td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; vertical-align: top; }
th { background: #f5f5f5; font-weight: 700; font-size: 9px; text-transform: uppercase; }
.added { color: #059669; font-weight: 700; } .edited { color: #d97706; font-weight: 700; } .removed { color: #dc2626; font-weight: 700; }
.status { color: #2563eb; font-weight: 700; } .meta { color: #666; font-size: 9px; }
.header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4px; }
.header h1 { font-size: 16px; font-weight: 700; margin: 0; } .header .right { text-align: right; font-size: 11px; color: #666; line-height: 1.7; }
.sep { border-top: 1px solid #ccc; margin: 10px 0 14px; }
.summary { display: flex; gap: 10px; margin-bottom: 14px; } .card { flex: 1; border: 1px solid #ddd; padding: 7px 10px; }
.card-label { font-size: 8px; text-transform: uppercase; letter-spacing: 0.5px; color: #666; margin-bottom: 1px; }
.card-value { font-size: 13px; font-weight: 700; }
.footer { margin-top: 20px; border-top: 1px solid #ccc; padding-top: 10px; display: flex; justify-content: space-between; font-size: 10px; color: #666; }
.day-hdr { background: #e8f0f8; padding: 4px 8px; font-weight: 700; font-size: 11px; border-bottom: 1px solid #ccc; margin-top: 12px; }
</style></head><body>${content.innerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); }, 300);
  };

  const eventCounts = events.reduce((acc, e) => {
    const t = e.event_type;
    if (t === 'LineAdded') acc.added++;
    else if (t === 'LineRemoved') acc.removed++;
    else if (t === 'LineModified') acc.edited++;
    else acc.other++;
    return acc;
  }, { added: 0, edited: 0, removed: 0, other: 0 });

  const grouped = groupByDay(events);

  const getRowClass = (type) => {
    if (type === 'LineAdded') return 'added';
    if (type === 'LineRemoved') return 'removed';
    if (type === 'LineModified') return 'edited';
    if (['SubmittedForReview', 'ApprovalGranted', 'ApprovalRejected', 'ReturnedToDraft', 'StatusChanged'].includes(type)) return 'status';
    return '';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-lg w-[960px] max-h-[90vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-100 border-b">
          <span className="text-sm font-semibold text-slate-800">BOM Change History — Print Preview</span>
          <div className="flex gap-2">
            <button onClick={handlePrint} data-testid="activity-print-confirm-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded text-xs font-medium hover:bg-slate-700 transition-colors">
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <button onClick={onClose}
              className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded text-xs font-medium hover:bg-slate-300 transition-colors">
              Close
            </button>
          </div>
        </div>
        <div className="overflow-y-auto max-h-[calc(90vh-48px)] p-5 bg-white">
          <div ref={printRef} style={{ fontFamily: 'monospace', fontSize: 11, color: '#222' }}>
            <div className="header">
              <div>
                <h1>BILL OF MATERIALS — CHANGE HISTORY REPORT</h1>
                <div className="meta" style={{ marginTop: 4 }}>{bomNumber} {bomName}</div>
              </div>
              <div className="right">
                <div>Revision: <strong>{revisionLabel || 'A.1'}</strong></div>
                <div>Report Date: {today}</div>
                <div>{events.length} total changes</div>
              </div>
            </div>
            <div className="sep" />

            <div className="summary">
              <div className="card"><div className="card-label">Total Changes</div><div className="card-value">{events.length}</div></div>
              <div className="card"><div className="card-label">Parts Added</div><div className="card-value" style={{ color: '#059669' }}>{eventCounts.added}</div></div>
              <div className="card"><div className="card-label">Parts Edited</div><div className="card-value" style={{ color: '#d97706' }}>{eventCounts.edited}</div></div>
              <div className="card"><div className="card-label">Parts Removed</div><div className="card-value" style={{ color: '#dc2626' }}>{eventCounts.removed}</div></div>
              <div className="card"><div className="card-label">Other Events</div><div className="card-value">{eventCounts.other}</div></div>
            </div>

            {grouped.map(group => (
              <div key={group.date}>
                <div className="day-hdr">{group.label} — {group.events.length} change{group.events.length !== 1 ? 's' : ''}</div>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 30 }}>#</th>
                      <th style={{ width: 55 }}>Time</th>
                      <th style={{ width: 75 }}>User</th>
                      <th style={{ width: 65 }}>Action</th>
                      <th>Change Description</th>
                      <th style={{ width: 120 }}>Before → After</th>
                      <th style={{ width: 40 }}>Clause</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.events.map((evt, idx) => {
                      const info = describeEvent(evt);
                      const cls = getRowClass(evt.event_type);
                      const cfg = EVENT_CONFIG[evt.event_type] || DEFAULT_CFG;
                      return (
                        <tr key={evt.event_id}>
                          <td style={{ textAlign: 'center', color: '#888' }}>{events.indexOf(evt) + 1}</td>
                          <td style={{ fontSize: 10 }}>{formatTime(evt.performed_at)}</td>
                          <td style={{ fontSize: 10 }}>{evt.performed_by_name || evt.performed_by}</td>
                          <td className={cls}>{cfg.label}</td>
                          <td>
                            <span style={{ fontWeight: 700 }}>{info.title}</span>
                            {info.detail && <><br /><span className="meta">{info.detail}</span></>}
                          </td>
                          <td style={{ fontSize: 10 }}>{info.meta || '—'}</td>
                          <td style={{ fontSize: 9, color: '#888' }}>{(evt.clause_references || []).join(', ')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}

            <div className="footer">
              <span>Generated from HADEN ATLAS PLM — AS9100D Change Management Evidence</span>
              <span>Printed: {today}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Main Activity Feed Panel ────────────────────────────────── */
export const ActivityFeedPanel = ({ bomId, revisionId, bomName, bomNumber, revisionLabel }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPrint, setShowPrint] = useState(false);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    if (!bomId) return;
    const tk = getToken();
    setLoading(true);
    fetchWithAuth(`${API}/api/v1/bom/${bomId}/audit-log`, {
      headers: { Authorization: `Bearer ${tk}` },
    })
      .then(r => r.json())
      .then(d => { setEvents(d); setLoading(false); })
      .catch(() => { toast.error('Failed to load activity feed'); setLoading(false); });
  }, [bomId]);

  const filteredEvents = filterType === 'all' ? events : events.filter(e => {
    if (filterType === 'added') return e.event_type === 'LineAdded';
    if (filterType === 'edited') return e.event_type === 'LineModified';
    if (filterType === 'removed') return e.event_type === 'LineRemoved';
    if (filterType === 'approval') return ['SubmittedForReview', 'ApprovalGranted', 'ApprovalRejected', 'ReturnedToDraft', 'StatusChanged'].includes(e.event_type);
    return true;
  });

  const grouped = groupByDay(filteredEvents);

  if (loading) return (
    <div className="flex items-center justify-center h-full text-slate-500 text-xs gap-2">
      <Clock className="w-4 h-4 animate-pulse" />
      <span>Loading activity...</span>
    </div>
  );

  return (
    <div className="flex flex-col h-full" data-testid="activity-feed-panel">
      {/* Toolbar */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-700/30">
        <Filter className="w-3 h-3 text-slate-500" />
        {[
          { key: 'all', label: 'All' },
          { key: 'added', label: 'Added' },
          { key: 'edited', label: 'Edited' },
          { key: 'removed', label: 'Removed' },
          { key: 'approval', label: 'Approvals' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilterType(f.key)}
            className={`px-1.5 py-0.5 rounded text-[9px] transition-colors ${filterType === f.key ? 'bg-blue-900/40 text-blue-400' : 'text-slate-500 hover:text-slate-300'}`}
            data-testid={`activity-filter-${f.key}`}>
            {f.label}
          </button>
        ))}
        <span className="text-[9px] text-slate-600 ml-auto">{filteredEvents.length} events</span>
        <button onClick={() => setShowPrint(true)}
          className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] bg-slate-700/50 hover:bg-slate-600/50 text-slate-400 hover:text-white transition-colors"
          data-testid="activity-print-btn">
          <Printer className="w-3 h-3" /> Print
        </button>
      </div>

      {/* Events list */}
      <div className="flex-1 overflow-y-auto px-2">
        {filteredEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500 text-xs gap-2">
            <Clock className="w-8 h-8 text-slate-700" />
            <span>No activity recorded yet</span>
            <span className="text-slate-600 text-[10px]">Changes will appear here as they happen</span>
          </div>
        ) : (
          grouped.map(group => (
            <div key={group.date}>
              <div className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold py-1.5 mt-2 border-b border-slate-700/20 sticky top-0 bg-slate-900/90 backdrop-blur-sm z-10"
                data-testid={`activity-day-${group.date}`}>
                {group.label} — {group.events.length} change{group.events.length !== 1 ? 's' : ''}
              </div>
              <div className="divide-y divide-slate-700/15">
                {group.events.map(evt => <EventCard key={evt.event_id} event={evt} />)}
              </div>
            </div>
          ))
        )}
      </div>

      {showPrint && (
        <PrintOverlay
          events={events}
          bomName={bomName}
          bomNumber={bomNumber}
          revisionLabel={revisionLabel}
          onClose={() => setShowPrint(false)}
        />
      )}
    </div>
  );
};
